import api from '../axiosConfig';
import { ROOMS, SETUP, BASE_URL_PMS } from '../config/apiEndpoints';

export const RoomService = {
    getAll: async (params = {}) => {
        try {
            if (!BASE_URL_PMS) {
                console.error('REACT_APP_API_BASE_URL_PMS is not defined in environment variables');
                throw new Error('API Configuration Error: PMS Base URL is missing');
            }

            const axiosParams = {};
            if (params.search) axiosParams.search = params.search;
            if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                if (params.page !== undefined) axiosParams.page = params.page + 1;
                if (params.limit) axiosParams.limit = params.limit;
            }
            if (params.status && params.status !== 'All') axiosParams.status = params.status;
            if (params.floorId) axiosParams.floorId = params.floorId;
            if (params.wingId) axiosParams.wingId = params.wingId;
            if (params.roomTypeId) axiosParams.roomTypeId = params.roomTypeId;
            if (params.sortBy) axiosParams.sortBy = params.sortBy;
            if (params.sortOrder) axiosParams.sortOrder = params.sortOrder;
            if (params.checkIn) axiosParams.checkIn = params.checkIn;
            if (params.checkOut) axiosParams.checkOut = params.checkOut;

            const response = await api.get(ROOMS.BASE, { params: axiosParams });
            return response.data;
        } catch (error) {
            console.error('RoomService getAll error:', error);
            throw error;
        }
    },

    save: async (room) => {
        try {
            const isEditing = !!room.id;
            const url = isEditing ? ROOMS.BY_ID(room.id) : ROOMS.BASE;

            const payload = {};
            if (room.roomNumber) payload.roomNumber = String(room.roomNumber);
            if (room.status) payload.status = room.status;
            if (room.floorId) payload.floorId = String(room.floorId);
            if (room.wingId) payload.wingId = String(room.wingId);
            if (room.roomTypeId) payload.roomTypeId = String(room.roomTypeId);
            if (room.type) payload.type = String(room.type);
            if (room.maxAdults !== undefined) payload.maxAdults = parseInt(room.maxAdults);
            if (room.maxKids !== undefined) payload.maxKids = parseInt(room.maxKids);
            if (room.baseRate !== undefined) payload.baseRate = parseFloat(room.baseRate);
            if (room.rate !== undefined && payload.baseRate === undefined) payload.baseRate = parseFloat(room.rate);
            if (room.amenities) payload.amenities = room.amenities;
            if (room.features && !payload.amenities) payload.amenities = room.features;
            if (room.images !== undefined) payload.images = Array.isArray(room.images) ? room.images : [];

            const response = isEditing
                ? await api.patch(url, payload)
                : await api.post(url, payload);

            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService save error:', error);
            throw error;
        }
    },

    getById: async (id) => {
        try {
            const response = await api.get(ROOMS.BY_ID(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService getById error:', error);
            throw error;
        }
    },

    archive: async (id) => {
        try {
            const response = await api.post(ROOMS.ARCHIVE(id), {});
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService archive error:', error);
            throw error;
        }
    },

    setOOO: async (id, payload) => {
        try {
            const response = await api.post(ROOMS.OUT_OF_ORDER(id), payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService setOOO error:', error);
            throw error;
        }
    },

    resolveOOO: async (id, oooId) => {
        try {
            const response = await api.post(ROOMS.RESOLVE_OOO(id, oooId), {});
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService resolveOOO error:', error);
            throw error;
        }
    },

    extendOOO: async (id, oooId, payload) => {
        try {
            const response = await api.post(ROOMS.EXTEND_OOO(id, oooId), payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService extendOOO error:', error);
            throw error;
        }
    },

    bulkUpdateStatus: async (payload) => {
        try {
            const response = await api.post(ROOMS.BULK_STATUS, payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService bulkUpdateStatus error:', error);
            throw error;
        }
    },

    getRoomTypes: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            if (params.limit) axiosParams.limit = params.limit;
            const response = await api.get(SETUP.ROOM_TYPES, { params: axiosParams });
            if (params.page !== undefined) return response.data;
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService getRoomTypes error:', error);
            throw error;
        }
    },

    saveRoomType: async (roomType) => {
        try {
            const isEditing = !!roomType.id;
            const url = isEditing ? `${SETUP.ROOM_TYPES}/${roomType.id}` : SETUP.ROOM_TYPES;
            
            const payload = {
                name: roomType.name,
                description: roomType.description,
                baseRate: parseFloat(roomType.baseRate) || 0,
                extraBedCharge: parseFloat(roomType.extraBedCharge) || 0,
                maxAdults: parseInt(roomType.maxAdults) || 0,
                maxKids: parseInt(roomType.maxKids) || 0,
                defaultAmenities: roomType.defaultAmenities || [],
                amenities: roomType.defaultAmenities || [],
                images: Array.isArray(roomType.images) ? roomType.images : [],
                status: roomType.status || 'Active'
            };

            const response = isEditing 
                ? await api.patch(url, payload)
                : await api.post(url, payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService saveRoomType error:', error);
            throw error;
        }
    },

    getFloors: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(SETUP.FLOORS, { params: axiosParams });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService getFloors error:', error);
            throw error;
        }
    },

    saveFloor: async (floor) => {
        try {
            const isEditing = !!floor.id;
            const url = isEditing ? `${SETUP.FLOORS}/${floor.id}` : SETUP.FLOORS;
            
            const payload = {
                number: parseInt(floor.number),
                label: floor.label,
                status: floor.status || 'Active'
            };

            const response = isEditing 
                ? await api.patch(url, payload)
                : await api.post(url, payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService saveFloor error:', error);
            throw error;
        }
    },

    getWings: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(SETUP.WINGS, { params: axiosParams });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService getWings error:', error);
            throw error;
        }
    },

    saveWing: async (wing) => {
        try {
            const isEditing = !!wing.id;
            const url = isEditing ? `${SETUP.WINGS}/${wing.id}` : SETUP.WINGS;
            
            const payload = {
                name: wing.name,
                status: wing.status || 'Active'
            };

            const response = isEditing 
                ? await api.patch(url, payload)
                : await api.post(url, payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService saveWing error:', error);
            throw error;
        }
    },

    getAmenities: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            if (params.limit) axiosParams.limit = params.limit;
            const response = await api.get(SETUP.AMENITIES, { params: axiosParams });
            if (params.page !== undefined) return response.data;
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService getAmenities error:', error);
            throw error;
        }
    },

    saveAmenity: async (amenity) => {
        try {
            const isEditing = !!amenity.id;
            const url = isEditing ? `${SETUP.AMENITIES}/${amenity.id}` : SETUP.AMENITIES;
            
            const payload = {
                name: amenity.name,
                category: amenity.category || 'General'
            };

            const response = isEditing 
                ? await api.patch(url, payload)
                : await api.post(url, payload);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService saveAmenity error:', error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            const response = await api.delete(ROOMS.BY_ID(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('RoomService delete error:', error);
            throw error;
        }
    },
};
